import { useState, useEffect, useCallback } from "react";
import { Loader2, RefreshCw, Download, Calculator, FileText, MailOpen, MousePointerClick, Mail, AlertTriangle, Clock, Inbox, Activity, User, MailWarning, Send, BellRing } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import NotificationSettings from "./NotificationSettings";

const TYPE_META = {
  form_submit: { icon: Inbox, label: "Form submitted", color: "text-flame border-flame/40 bg-flame/10" },
  asset_download: { icon: Download, label: "Asset downloaded", color: "text-amber-400 border-amber-400/40 bg-amber-400/10" },
  calculator_complete: { icon: Calculator, label: "Calculator completed", color: "text-flame border-flame/40 bg-flame/10" },
  calculator_open: { icon: Calculator, label: "Calculator opened", color: "text-white/60 border-white/15 bg-white/5" },
  collateral_view: { icon: FileText, label: "Collateral viewed", color: "text-blue-400 border-blue-400/40 bg-blue-400/10" },
  email_open: { icon: MailOpen, label: "Email opened", color: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10" },
  email_click: { icon: MousePointerClick, label: "Email link clicked", color: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10" },
  email_delivered: { icon: Mail, label: "Email delivered", color: "text-white/50 border-white/15 bg-white/5" },
  email_sent: { icon: Mail, label: "Email sent", color: "text-white/50 border-white/15 bg-white/5" },
  email_bounced: { icon: AlertTriangle, label: "Email bounced", color: "text-red-400 border-red-400/40 bg-red-400/10" },
  email_complained: { icon: AlertTriangle, label: "Marked as spam", color: "text-red-400 border-red-400/40 bg-red-400/10" },
  page_engagement: { icon: Clock, label: "Page engagement", color: "text-white/50 border-white/15 bg-white/5" },
};

const FILTERS = [
  { k: "all", label: "All activity" },
  { k: "high", label: "High-value only" },
  { k: "asset_download", label: "Downloads" },
  { k: "calculator_complete", label: "Calculator completions" },
  { k: "collateral_view", label: "Collateral views" },
  { k: "email_open", label: "Email opens" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return "just now";
  const m = Math.floor(secs / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function ActivityFeed() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, high_value_total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [warmSilent, setWarmSilent] = useState([]);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filter === "high") params.high_value = true;
      else if (filter !== "all") params.etype = filter;
      const { data } = await api.get("/admin/activity", { params });
      setItems(data.items || []);
      setSummary({ total: data.total || 0, high_value_total: data.high_value_total || 0 });
    } catch (e) { toast.error("Failed to load activity"); }
    finally { setLoading(false); }
  }, [filter]);

  const loadWarmSilent = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/warm-silent");
      setWarmSilent(data.items || []);
    } catch (e) { /* non-critical */ }
  }, []);

  const sendDailyDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/send-daily-digest");
      toast.success(`Daily digest emailed — ${data.new_leads} leads, ${data.downloads} downloads, ${data.email_opens} opens`);
    } catch (e) { toast.error("Failed to send daily digest"); }
    finally { setSendingDigest(false); }
  };

  const [postingScorecard, setPostingScorecard] = useState(false);
  const postScorecard = async () => {
    setPostingScorecard(true);
    try {
      const { data } = await api.post("/admin/send-scorecard");
      toast.success(`Scorecard posted to Slack — ${data.new_leads} leads, CTR ${data.outreach_ctr}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to post scorecard"); }
    finally { setPostingScorecard(false); }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadWarmSilent(); }, [loadWarmSilent]);
  useEffect(() => {
    const id = setInterval(() => { load(); loadWarmSilent(); }, 20000);
    return () => clearInterval(id);
  }, [load, loadWarmSilent]);

  return (
    <div data-testid="activity-feed">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-black text-2xl">Engagement Activity</h2>
        <div className="flex items-center gap-2">
          <button data-testid="toggle-settings" onClick={() => setShowSettings((s) => !s)}
            className={`inline-flex items-center gap-2 border px-4 py-2 text-sm font-semibold transition-colors ${showSettings ? "border-flame text-flame" : "border-white/15 text-white/70 hover:text-flame hover:border-flame/50"}`}>
            <BellRing className="h-4 w-4" /> Notifications
          </button>
          <button data-testid="post-scorecard" onClick={postScorecard} disabled={postingScorecard}
            className="inline-flex items-center gap-2 border border-white/15 text-white/70 hover:text-flame hover:border-flame/50 px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50">
            {postingScorecard ? <Loader2 className="h-4 w-4 animate-spin" /> : <Activity className="h-4 w-4" />} Post scorecard to Slack
          </button>
          <button data-testid="send-daily-digest" onClick={sendDailyDigest} disabled={sendingDigest}
            className="inline-flex items-center gap-2 border border-white/15 text-white/70 hover:text-flame hover:border-flame/50 px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50">
            {sendingDigest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send daily digest
          </button>
        </div>
      </div>

      {showSettings && <NotificationSettings />}

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-6">
        <div className="bg-ink border border-white/10 p-6">
          <div className="flex items-center justify-between"><Activity className="h-5 w-5 text-flame" /><span className="font-display font-black text-3xl">{summary.total}</span></div>
          <div className="mt-3 text-xs text-white/50 uppercase tracking-widest">Total events</div>
        </div>
        <div className="bg-ink border border-white/10 p-6">
          <div className="flex items-center justify-between"><Inbox className="h-5 w-5 text-flame" /><span className="font-display font-black text-3xl">{summary.high_value_total}</span></div>
          <div className="mt-3 text-xs text-white/50 uppercase tracking-widest">High-value events</div>
        </div>
      </div>

      {/* Warm but silent leads */}
      {warmSilent.length > 0 && (
        <div className="mb-6 border border-amber-400/30 bg-amber-400/[0.04]" data-testid="warm-silent-panel">
          <div className="flex items-center gap-2 p-4 border-b border-amber-400/20">
            <MailWarning className="h-5 w-5 text-amber-400" />
            <h3 className="font-display font-bold text-sm uppercase tracking-widest text-amber-400">Warm but silent</h3>
            <span className="text-xs text-white/40">— opened 3+ emails, never submitted. Prime for a personal nudge.</span>
          </div>
          <div className="divide-y divide-white/10">
            {warmSilent.map((w) => (
              <div key={w.email} data-testid={`warm-silent-${w.email}`} className="p-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{w.name || w.email}{w.company ? ` · ${w.company}` : ""}</div>
                  <div className="text-xs text-white/50 truncate">{w.email}</div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 border border-amber-400/40 text-amber-400 bg-amber-400/10">
                    <MailOpen className="h-3 w-3" /> {w.opens} opens
                  </span>
                  <a href={`mailto:${w.email}?subject=Following up — OneHarvest`} className="inline-flex items-center gap-1.5 bg-flame text-white px-3 py-1.5 text-xs font-semibold hover:bg-flame/90 transition-colors"><Mail className="h-3.5 w-3.5" /> Nudge</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="activity-filters">
        {FILTERS.map((f) => (
          <button key={f.k} data-testid={`activity-filter-${f.k}`} onClick={() => setFilter(f.k)}
            className={`px-3 py-2 text-sm border transition-colors ${filter === f.k ? "bg-flame text-white border-flame" : "border-white/15 text-white/60 hover:text-white"}`}>
            {f.label}
          </button>
        ))}
        <button data-testid="activity-refresh" onClick={load} className="ml-auto p-2 text-white/60 hover:text-flame transition-colors"><RefreshCw className="h-4 w-4" /></button>
      </div>

      <div className="bg-ink border border-white/10">
        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-flame" /></div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center text-white/40" data-testid="activity-empty">No engagement activity yet. Events appear here as visitors interact with your site and emails.</div>
        ) : (
          <div className="divide-y divide-white/10">
            {items.map((it) => {
              const meta = TYPE_META[it.type] || { icon: Activity, label: it.type, color: "text-white/50 border-white/15 bg-white/5" };
              const Icon = meta.icon;
              const who = it.name || it.email || "Anonymous visitor";
              return (
                <div key={it.id} data-testid={`activity-item-${it.id}`} className={`p-4 flex items-start gap-4 ${it.high_value ? "bg-flame/[0.04]" : ""}`}>
                  <div className={`h-9 w-9 flex items-center justify-center border flex-shrink-0 ${meta.color}`}><Icon className="h-4 w-4" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-widest text-white/50">{meta.label}</span>
                      {it.product && <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-white/10 text-white/40">{it.product}</span>}
                      {it.high_value && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-flame/40 text-flame">High value</span>}
                    </div>
                    <div className="mt-1 font-medium text-sm truncate">{it.label || it.path || meta.label}</div>
                    <div className="mt-0.5 text-xs text-white/50 flex items-center gap-1.5">
                      <User className="h-3 w-3" />
                      {it.email ? <span className="text-white/70">{who} · {it.email}</span> : <span>{who}</span>}
                    </div>
                  </div>
                  <div className="text-[11px] text-white/40 whitespace-nowrap flex-shrink-0">{timeAgo(it.created_at)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
